import { AppIcon } from '@app/components/AppIcon';
import { capitalizeFirstLetter } from '@app/system/utils/basic';
import { Button, Divider, FormControl, FormLabel } from '@mui/joy';
import { useState } from 'react';
import RenderFormType, { FormType } from './renderFormType';

import AccordionGroup from '@mui/joy/AccordionGroup';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';

type FormData = {
    [key: string]: {
        title: string;
        value: string;
        type: FormType;
    };
};

const mainFields: FormData = {
    raison_sociale: { title: 'RAISON SOCIALE', value: '', type: 'text' },
    adresse: { title: 'ADRESSE', value: '', type: 'text' },
    NPA: { title: 'CODE POSTAL', value: '', type: 'string' },
    localité: { title: 'LOCALITE', value: '', type: 'string' },
    email: { title: 'EMAIL', value: '', type: 'string' },
    titre: { title: 'TITRE', value: '', type: 'string' },
    prénom: { title: 'PRENOM', value: '', type: 'string' },
    nom: { title: 'NOM', value: '', type: 'string' },
    téléphone: { title: 'TELEPHONE', value: '', type: 'number' },
    date_accord: { title: 'DATE ACCORD', value: '', type: 'date' },
    id_club: { title: 'NO CLUB', value: '', type: 'clubs' },
    id_collaborateur: {
        title: 'COLLABORATEUR-TRICE',
        value: '',
        type: 'collaborators',
    },
    CHF_promesse: { title: 'PROMESSE CHF', value: '', type: 'number' },
    publicité: { title: 'PUBLICITE', value: '', type: 'string' },
    infos_internes: { title: 'INFOS INTERNES', value: '', type: 'text' },
};

const secondFields: FormData = {
    prénom_2: { title: 'PRENOM 2', value: '', type: 'string' },
    nom_2: { title: 'NOM 2', value: '', type: 'string' },
    téléphone_2: { title: 'TELEPHONE 2', value: '', type: 'number' },
    case_Postale: { title: 'CASE POSTALE', value: '', type: 'string' },
    raison_sociale_pj: {
        title: 'RAISON SOCIALE PJ',
        value: '',
        type: 'text',
    },
    libellé_facture: { title: 'LIBELLE FACTURE', value: '', type: 'text' },
    correspondance: { title: 'CORRESPONDANCE', value: '', type: 'text' },
    accord_de: { title: 'ACCORD DE', value: '', type: 'string' },
};

const ImportParrainagesForm = ({
    onSubmit,
}: {
    onSubmit: (data: any) => void;
}) => {
    const [mainFormData, setMainFormData] = useState<FormData>(mainFields);
    const [secondFormData, setSecondFormData] =
        useState<FormData>(secondFields);

    const [showSecondForm, setShowSecondForm] = useState(false);

    const handleChangeMain = (value: string, key: string) => {
        const newFormData = {
            ...mainFormData,
            [key]: { ...mainFormData[key], value: value },
        };
        setMainFormData(newFormData);
    };
    const handleChangeSecond = (value: string, key: string) => {
        const newFormData = {
            ...secondFormData,
            [key]: { ...secondFormData[key], value: value },
        };
        setSecondFormData(newFormData);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        const formData = { ...mainFormData, ...secondFormData };
        const data = Object.entries(formData)
            .map(([key, field]) =>
                field.value ? { [key]: field.value } : null
            )
            .filter((item) => item !== null);
        const dataObject = data.reduce((acc, curr) => {
            acc[Object.keys(curr)[0]] = Object.values(curr)[0];
            return acc;
        }, {});
        onSubmit(dataObject);
    };

    return (
        <div>
            <form
                onSubmit={handleSubmit}
                className="flex flex-col items-center gap-4"
            >
                <div className="flex w-full items-center justify-between">
                    <div>Parrainage</div>
                    <Button
                        startDecorator={<AppIcon icon="CloudArrowUpIcon" />}
                        type="submit"
                    >
                        Importer
                    </Button>
                </div>
                <Divider />
                <div className="gap-4">
                    <div className="mb-8 grid grid-cols-3 gap-4">
                        {Object.entries(mainFormData).map(
                            ([key, field], index) => (
                                <FormControl
                                    key={key}
                                    className={`col-span-${
                                        Math.floor(index / 3) + 1
                                    }`}
                                >
                                    <FormLabel className="-pb-2 -mb-2 text-xs">
                                        {capitalizeFirstLetter(field.title)}
                                    </FormLabel>
                                    <RenderFormType
                                        type={field.type as FormType}
                                        value={field.value}
                                        name={key}
                                        onChange={(e: any) =>
                                            handleChangeMain(e, key)
                                        }
                                    />
                                </FormControl>
                            )
                        )}
                    </div>
                    <div
                        className="mb-4 cursor-pointer"
                        onClick={() => setShowSecondForm(!showSecondForm)}
                    >
                        <div className="mb-2 flex items-center justify-between">
                            <div>+ Informations supplémentaires</div>
                            <AppIcon
                                icon={
                                    showSecondForm
                                        ? 'ChevronUpIcon'
                                        : 'ChevronDownIcon'
                                }
                            />
                        </div>
                        <Divider />
                    </div>
                    {showSecondForm && (
                        <div className="grid grid-cols-3 gap-4">
                            {Object.entries(secondFormData).map(
                                ([key, field], index) => (
                                    <FormControl
                                        key={key}
                                        className={`col-span-${
                                            Math.floor(index / 5) + 1
                                        }`}
                                    >
                                        <FormLabel className="-pb-2 -mb-2 text-xs">
                                            {capitalizeFirstLetter(field.title)}
                                        </FormLabel>
                                        <RenderFormType
                                            type={field.type as FormType}
                                            value={field.value}
                                            name={key}
                                            onChange={(e: any) =>
                                                handleChangeSecond(e, key)
                                            }
                                        />
                                    </FormControl>
                                )
                            )}
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default ImportParrainagesForm;
